import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import BrochureGate from "@/components/BrochureGate";
import { 
  Hotel, CheckCircle, Award, Shield, Calendar, 
  Phone, Mail, MapPin, Star, ArrowRight, Download,
  Clock, Users, Sparkles, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HotelResortPage() {
  const [scrolled, setScrolled] = useState(false);
  const [showBrochureGate, setShowBrochureGate] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const membershipTiers = [
    {
      name: "Boutique Care",
      duration: "6 Months",
      propertySize: "Under 100 Rooms",
      features: [
        "Weekly property inspections",
        "Guest-area tree maintenance",
        "Pool & patio landscaping",
        "Dedicated account manager",
        "Priority storm response",
        "Seasonal aesthetic updates",
      ],
      highlighted: false,
    },
    {
      name: "Resort Excellence",
      duration: "12 Months",
      propertySize: "100-500 Rooms",
      features: [
        "Bi-weekly property visits",
        "Complete grounds maintenance",
        "Fire safety compliance",
        "24/7 priority emergency",
        "Quarterly ISA assessments",
        "Event preparation service",
        "Custom uniform program",
        "Guest safety protocols",
        "Landscape coordinator",
      ],
      highlighted: true,
      badge: "Most Popular",
    },
    {
      name: "Grand Luxury",
      duration: "Multi-Year",
      propertySize: "500+ Rooms",
      features: [
        "All Resort Excellence features",
        "Daily walkthrough inspections",
        "VIP guest area priority",
        "Locked-in pricing",
        "Exclusive scheduling",
        "Staff training program",
        "Quarterly executive reviews",
        "Multi-property discounts",
      ],
      highlighted: false,
      badge: "Enterprise",
    },
  ];

  const serviceAreas = [
    { name: "Scottsdale", zips: ["85250", "85251", "85254", "85255", "85258", "85259", "85260", "85262"] },
    { name: "Paradise Valley", zips: ["85253"] },
    { name: "Phoenix - Biltmore", zips: ["85016", "85014", "85018"] },
    { name: "Phoenix - Camelback", zips: ["85016", "85018"] },
    { name: "Tempe", zips: ["85281", "85282"] },
    { name: "Fountain Hills", zips: ["85268", "85269"] },
  ];

  const premierProperties = [
    "The Phoenician",
    "Four Seasons Scottsdale",
    "Fairmont Scottsdale Princess",
    "The Scottsdale Resort",
    "Hyatt Regency Scottsdale",
    "JW Marriott Camelback Inn",
    "Omni Scottsdale Resort",
    "Andaz Scottsdale Resort",
  ];

  return (
    <>
      <Helmet>
        <title>Hotel & Resort Tree Care | Celebrity Tree Service | Scottsdale, Paradise Valley, Phoenix AZ</title>
        <meta name="description" content="Premium hotel and resort landscaping services in Scottsdale, Paradise Valley, and Phoenix Arizona. ISA Certified Arborists. Luxury property tree care for Arizona's finest resorts." />
        <meta name="keywords" content="hotel tree care, resort landscaping, Scottsdale hotel maintenance, Paradise Valley resort trees, Phoenix hotel landscaping, luxury resort tree service" />
        <link rel="canonical" href="https://paradisevalleytreeservice.com/hotels-resorts" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F9F5]" data-testid="hotel-resort-page">
        <Navigation scrolled={scrolled} />

        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] flex items-center pt-32"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1770021601330-888e30209a60?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-transparent" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#D4A853]/20 border border-[#D4A853]/40 px-4 py-2 mb-6">
                <Hotel className="w-4 h-4 text-[#D4A853]" />
                <span className="text-[#D4A853] text-sm font-semibold uppercase tracking-wider">
                  Luxury Hospitality Partners
                </span>
              </div>
              
              <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                World-Class
                <br />
                <span className="text-[#D4A853]">Resort Care</span>
              </h1>
              
              <p className="text-white/80 text-xl mb-8 max-w-2xl">
                Arizona's premier hotels and resorts trust Celebrity Tree Service for 
                immaculate grounds that enhance the guest experience. Discreet, professional 
                service that maintains your property's luxury reputation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => setShowBrochureGate(true)}
                  className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                  data-testid="download-brochure-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Brochure
                </Button>
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                >
                  <a href="tel:4803517775">Call (480) 351-7775</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-[#0A1628] py-8 border-y border-[#D4A853]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: Award, label: "ISA Certified", sub: "Arborists" },
                { icon: Shield, label: "Fully Insured", sub: "$5M Coverage" },
                { icon: Clock, label: "Minimal Disruption", sub: "Guest-First" },
                { icon: Users, label: "Since 2015", sub: "Trusted Partner" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-3">
                  <item.icon className="w-8 h-8 text-[#D4A853]" />
                  <div className="text-left">
                    <p className="text-white font-bold">{item.label}</p>
                    <p className="text-white/60 text-sm">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Tiers */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#D4A853] text-xs tracking-[0.3em] uppercase mb-4">
                Hospitality Programs
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Hotel & Resort Memberships
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
                Comprehensive landscaping solutions tailored for luxury hospitality. 
                Every program includes dedicated management and guest-conscious scheduling.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {membershipTiers.map((tier, idx) => (
                <div 
                  key={idx}
                  className={`relative p-8 ${
                    tier.highlighted 
                      ? "bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] text-white border-2 border-[#D4A853]"
                      : "bg-[#F8F6F1] border border-[#D4A853]/20"
                  }`}
                  data-testid={`membership-tier-${idx}`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A853] text-[#0A1628] px-4 py-1 text-xs font-bold uppercase tracking-wider">
                      {tier.badge}
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <p className={`text-sm font-semibold uppercase tracking-wider mb-1 ${tier.highlighted ? "text-[#D4A853]" : "text-[#D4A853]"}`}>
                      {tier.duration}
                    </p>
                    <h3 className={`font-['Cormorant_Garamond'] text-2xl font-bold mb-2 ${tier.highlighted ? "text-white" : "text-[#0A1628]"}`}>
                      {tier.name}
                    </h3>
                    <p className={`text-sm ${tier.highlighted ? "text-white/60" : "text-[#64748B]"}`}>
                      {tier.propertySize}
                    </p>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {tier.features.map((feature, fidx) => (
                      <div key={fidx} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? "text-[#D4A853]" : "text-[#10B981]"}`} />
                        <span className={tier.highlighted ? "text-white/90" : "text-[#64748B]"}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    asChild
                    className={`w-full rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold ${
                      tier.highlighted 
                        ? "bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628]"
                        : "bg-[#0A1628] text-white hover:bg-[#1E3A5F]"
                    }`}
                  >
                    <a href="/#contact">Request Custom Quote</a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Uniform Customization Notice */}
        <section className="py-16 bg-gradient-to-r from-[#D4A853]/10 to-[#D4A853]/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Sparkles className="w-10 h-10 text-[#D4A853] mx-auto mb-4" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628] mb-4">
              Seamless Brand Integration
            </h3>
            <p className="text-[#64748B] text-lg mb-4">
              Our team can wear custom uniforms featuring your hotel's logo and branding, 
              ensuring our crew presents as seamlessly as your own staff. This white-glove 
              service maintains the luxury experience your guests expect.
            </p>
            <p className="text-[#0A1628] font-semibold">
              <span className="text-[#D4A853]">Note:</span> Custom uniform costs are covered by the property 
              as part of the partnership onboarding process.
            </p>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-24 bg-[#FDFCF9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#D4A853] text-xs tracking-[0.3em] uppercase mb-4">
                Service Coverage
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Premium Service Areas
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto">
                Serving Arizona's most prestigious hospitality properties within 15 miles of 
                Scottsdale and Paradise Valley. Now expanding to California locations.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {serviceAreas.map((area, idx) => (
                <div key={idx} className="bg-white p-6 border border-[#D4A853]/20">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-[#D4A853]" />
                    <h3 className="font-bold text-[#0A1628]">{area.name}</h3>
                  </div>
                  <p className="text-[#64748B] text-sm">
                    Zip Codes: {area.zips.join(", ")}
                  </p>
                </div>
              ))}
            </div>

            {/* California Expansion */}
            <div className="bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] p-8 text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-4">
                Multi-Property Portfolio?
              </h3>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                Celebrity Tree Service now serves select California locations. If your hospitality 
                group manages properties in both Arizona and California, we offer consolidated 
                service agreements with multi-property discounts.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="tel:4803517775">Discuss Multi-Property Service</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trusted By Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center font-['Cinzel'] text-[#64748B] text-xs tracking-[0.3em] uppercase mb-8">
              Trusted by Arizona's Premier Hotels & Resorts
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {premierProperties.map((property, idx) => (
                <div key={idx} className="text-center py-4 px-6 bg-[#F8F6F1]">
                  <p className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#0A1628]">{property}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-white mb-6">
              Enhance Your Guest Experience
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Partner with Celebrity Tree Service and ensure your property's grounds 
              match the luxury experience you promise your guests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setShowBrochureGate(true)}
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Resort Brochure
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="/#contact">Schedule Property Tour</a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Brochure Gate Modal */}
      {showBrochureGate && (
        <BrochureGate 
          type="hotel-resort" 
          onClose={() => setShowBrochureGate(false)} 
        />
      )}
    </>
  );
}
